import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Loader2, Users, Store, Package, ShoppingCart, TrendingUp, ArrowUpRight, CheckCircle2, Clock, Truck, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import AdminLayout from "./AdminLayout";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadStats = async () => {
    try {
      const response = await api.get("/admin/stats");
      setStats(response.data);
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStats();
  }, []);

  if (loading) {
    return (
      <AdminLayout title="لوحة التحكم" subtitle="نظرة عامة وإحصائيات المنصة">
        <div className="h-64 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </AdminLayout>
    );
  }

  const statItems = [
    { title: "إجمالي المستخدمين", value: stats?.users?.total || 0, subtitle: `${stats?.users?.buyers || 0} مشترين · ${stats?.users?.sellers || 0} بائعين`, icon: Users, color: "text-blue-600", bg: "bg-blue-50" },
    { title: "المتاجر المفتوحة", value: stats?.shops?.open || 0, subtitle: `من أصل ${stats?.shops?.total || 0} متجر`, icon: Store, color: "text-emerald-600", bg: "bg-emerald-50" },
    { title: "المنتجات النشطة", value: stats?.products?.active || 0, subtitle: `من أصل ${stats?.products?.total || 0} منتج`, icon: Package, color: "text-purple-600", bg: "bg-purple-50" },
    { title: "الطلبات المكتملة", value: stats?.orders?.by_status?.delivered || 0, subtitle: `من أصل ${stats?.orders?.total || 0} طلب`, icon: CheckCircle2, color: "text-teal-600", bg: "bg-teal-50" },
    { title: "إجمالي المبيعات", value: `${(stats?.financials?.total_revenue || 0).toLocaleString()} ل.س`, subtitle: "قيمة الطلبات المستلمة", icon: TrendingUp, color: "text-amber-600", bg: "bg-amber-50" },
    { title: "أجور التوصيل", value: `${(stats?.financials?.total_delivery_fees || 0).toLocaleString()} ل.س`, subtitle: "مستحقات السائقين", icon: Truck, color: "text-indigo-600", bg: "bg-indigo-50" },
  ];

  return (
    <AdminLayout title="لوحة التحكم" subtitle="نظرة شاملة وسريعة على كافة عمليات المنصة">
      {/* Quick Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-8">
        {statItems.map((item, index) => {
          const Icon = item.icon;
          return (
            <Card key={index} className="border border-border/80 shadow-sm hover:shadow transition-shadow">
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-muted-foreground">{item.title}</p>
                    <h3 className="text-2xl font-bold mt-1 tracking-tight">{item.value}</h3>
                    <p className="text-xs text-muted-foreground mt-1.5">{item.subtitle}</p>
                  </div>
                  <div className={`p-3 rounded-xl ${item.bg}`}>
                    <Icon className={`w-6 h-6 ${item.color}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Operations Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <Card className="lg:col-span-2 border border-border/80 shadow-sm">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg">حالات الطلبات</CardTitle>
                <CardDescription>توزيع كافة طلبات الزبائن حسب مسار التنفيذ</CardDescription>
              </div>
              <Link to="/admin/orders">
                <Button variant="ghost" size="sm" className="text-xs text-primary gap-1">
                  عرض جميع الطلبات <ArrowUpRight className="w-3.5 h-3.5" />
                </Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="p-4 rounded-xl border border-border bg-card flex flex-col items-center justify-center text-center">
                <span className="text-xs text-muted-foreground mb-1">قيد الانتظار</span>
                <span className="text-2xl font-bold text-amber-600">{stats?.orders?.by_status?.pending || 0}</span>
                <span className="text-[10px] text-muted-foreground mt-1">بانتظار قبول المتجر</span>
              </div>
              <div className="p-4 rounded-xl border border-border bg-card flex flex-col items-center justify-center text-center">
                <span className="text-xs text-muted-foreground mb-1">جاري التحضير</span>
                <span className="text-2xl font-bold text-blue-600">{stats?.orders?.by_status?.preparing || 0}</span>
                <span className="text-[10px] text-muted-foreground mt-1">قيد التجهيز بالمحل</span>
              </div>
              <div className="p-4 rounded-xl border border-border bg-card flex flex-col items-center justify-center text-center">
                <span className="text-xs text-muted-foreground mb-1">في الطريق</span>
                <span className="text-2xl font-bold text-indigo-600">{stats?.orders?.by_status?.out_for_delivery || 0}</span>
                <span className="text-[10px] text-muted-foreground mt-1">مع مندوب التوصيل</span>
              </div>
              <div className="p-4 rounded-xl border border-border bg-card flex flex-col items-center justify-center text-center">
                <span className="text-xs text-muted-foreground mb-1">ملغية</span>
                <span className="text-2xl font-bold text-rose-600">{stats?.orders?.by_status?.cancelled || 0}</span>
                <span className="text-[10px] text-muted-foreground mt-1">طلبات مرفوضة/ملغية</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-border/80 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg">إدارة سريعة</CardTitle>
            <CardDescription>روابط وإجراءات فورية</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Link to="/admin/users" className="block">
              <div className="p-3 rounded-lg border border-border hover:bg-muted/50 transition-colors flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Users className="w-4 h-4 text-primary" />
                  <span className="text-sm font-medium">إضافة مستخدم جديد</span>
                </div>
                <ArrowUpRight className="w-4 h-4 text-muted-foreground" />
              </div>
            </Link>
            <Link to="/admin/shops" className="block">
              <div className="p-3 rounded-lg border border-border hover:bg-muted/50 transition-colors flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Store className="w-4 h-4 text-primary" />
                  <span className="text-sm font-medium">إنشاء متجر جديد</span>
                </div>
                <ArrowUpRight className="w-4 h-4 text-muted-foreground" />
              </div>
            </Link>
            <Link to="/admin/products" className="block">
              <div className="p-3 rounded-lg border border-border hover:bg-muted/50 transition-colors flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Package className="w-4 h-4 text-primary" />
                  <span className="text-sm font-medium">إضافة منتج وتحديد السعر</span>
                </div>
                <ArrowUpRight className="w-4 h-4 text-muted-foreground" />
              </div>
            </Link>
            <Link to="/admin/whatsapp" className="block">
              <div className="p-3 rounded-lg border border-border hover:bg-muted/50 transition-colors flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                  <span className="text-sm font-medium">حالة اتصال واتساب</span>
                </div>
                <ArrowUpRight className="w-4 h-4 text-muted-foreground" />
              </div>
            </Link>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
